import React from 'react';
import { toneClasses } from '../lib/tones';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export type InlineNoticeTone = 'info' | 'warning' | 'danger' | 'success';

export interface InlineNoticeProps {
    /** What the notice MEANS (see `tones.ts`): `warning` needs attention,
     *  `danger` is broken or blocking, `info` explains, `success` confirms. */
    tone: InlineNoticeTone;
    /** Material Symbol name. Defaults to the tone's own icon
     *  (`info` / `warning` / `error` / `check_circle`). */
    icon?: IconName;
    /** The one-line headline, in semibold. */
    title?: React.ReactNode;
    /** The explanation, in regular weight. Several lines are fine. */
    children?: React.ReactNode;
    /** A control at the right end (typically a `Button size="sm"`). */
    action?: React.ReactNode;
    className?: string;
}

const DEFAULT_ICON: Record<InlineNoticeTone, IconName> = {
    info: 'info',
    warning: 'warning',
    danger: 'error',
    success: 'check_circle',
};

const isEmpty = (node: React.ReactNode) => node === null || node === undefined || node === false || node === '';

/**
 * A notice that sits INSIDE the page flow — above a form, under a heading,
 * inside a card — as opposed to a toast (transient) or a `ConfirmDialog`
 * (blocking). One tinted, bordered row: icon, text, optional action.
 *
 * The colours come from `toneClasses` (the pill fill, border and text of the
 * tone), so amber means the same here as in a `Badge` or a stat card. Never
 * hand-roll the `bg-amber-50 border-amber-200 …` strip again: that is how the
 * app ended up with a different amber per screen.
 *
 * - `title` only → a single emphasised line (section notices, hero banners);
 * - `children` only → a paragraph (explanations);
 * - both → headline + explanation.
 *
 * @example
 * <InlineNotice tone="warning">{t('settings.fiscal.holder.vatNumberHint')}</InlineNotice>
 * <InlineNotice tone="danger" icon="edit_note" title={t('orderDetail.marketplaceEdited')} />
 * <InlineNotice tone="info" title={t(msg)} action={<Button size="sm">…</Button>} />
 */
export default function InlineNotice({ tone, icon, title, children, action, className = '' }: InlineNoticeProps) {
    const c = toneClasses[tone];
    const hasTitle = !isEmpty(title);
    const hasBody = !isEmpty(children);
    return (
        <div
            className={`flex flex-wrap gap-2 rounded-lg border px-3 py-2 ${hasBody ? 'items-start' : 'items-center'} ${c.pillBg} ${c.pillBorder} ${className}`}
        >
            {/* Icon + text are ONE flex item so the action is the only thing
                that can wrap. On a phone the group asks for its full text width
                (`flex-auto`), so a text that does not fit beside the action
                pushes the action under it (right-aligned) instead of squeezing
                itself into a narrow column; a short text keeps it inline. From
                `md` up the group starts at zero width and the action always
                stays at the right end, as before. */}
            <div className={`flex min-w-0 flex-auto md:flex-1 gap-2 ${hasBody ? 'items-start' : 'items-center'}`}>
                <Icon name={icon ?? DEFAULT_ICON[tone]} className={`!text-[18px] shrink-0 ${hasBody ? 'mt-px' : ''} ${c.icon}`} aria-hidden="true" />
                <div className={`min-w-0 flex-1 text-xs ${c.pillText}`}>
                    {hasTitle && <div className="font-semibold">{title}</div>}
                    {hasBody && <div className={`leading-relaxed ${hasTitle ? 'mt-0.5' : ''}`}>{children}</div>}
                </div>
            </div>
            {!isEmpty(action) && <div className="ml-auto shrink-0">{action}</div>}
        </div>
    );
}
